import { DataHandlerCalibration, DataHandlerSnapshot, Device, Snapshot } from "luxedo-data"
import {
	CalibrationProgress,
	DeviceCalibrationManager,
} from "../../../../modules/device-operation-managers/DeviceCalibrationManager"
import { closeOverlay, openOverlay } from "overlay"
import CalibrationOverlay from "./calibration-overlay/CalibrationOverlay.svelte"
import { Unsubscriber } from "svelte/store"
import { openMinimizedCalibration } from "./calibration-minimized"

export enum CalibrationStep {
	"Instructions" = 0,
	"Loading" = 1,
	"Finished" = 2,
	"Failed" = -1,
	"Troubleshooter" = -2,
}

type CalibrationState = {
	step: CalibrationStep
	progress: CalibrationProgress
	failImages?: Array<string>
	snapshot?: Snapshot
}

interface DeviceIdMap<T> {
	[index: Device["id"]]: T
}

class CalController {
	readonly STATE_DEFAULT: CalibrationState = {
		step: CalibrationStep.Instructions,
		progress: {
			progress: 0,
			message: "Initializing...",
			description:
				"Calibration is the process of projecting patterns from your Luxedo, taking pictures of the projection using the onboard camera, then processing the images to create a snapshot that truly reflects your projection space.",
		},
	}

	// keyed by device id
	runs: DeviceIdMap<CalibrationState> = $state({})
	overlayIds: DeviceIdMap<string> = {}
	unsubscribers: DeviceIdMap<Unsubscriber> = {}

	/**
	 * Open the calibration overlay for the spcified device
	 * @param device
	 */
	open = (device: Device) => {
		if (!(device.id in this.runs)) this.runs[device.id] = this.STATE_DEFAULT

		this.overlayIds[device.id] = openOverlay(CalibrationOverlay, {
			heading: `Calibrating ${device.name}`,
			classHeading: "no-underline",
			classOverlay: "calibration-overlay",
			props: {
				device,
			},

			beforeClose: async () => {
				if (!(device.id in this.runs)) return

				const step = this.runs[device.id].step
				if ([CalibrationStep.Loading, CalibrationStep.Failed].includes(step))
					openMinimizedCalibration(device)
				else {
					DeviceCalibrationManager.cleanUp(device)
					this.reset(device.id)
				}
			},
		})
	}

	/**
	 * Restarts a calibration for the spcified device
	 * @param device
	 */
	retry = (device: Device) => {
		this.reset(device.id)
		this.calibrate(device)
	}

	/**
	 * Resets calibration state for the specified device id
	 * @param deviceId
	 */
	reset = (deviceId: Device["id"]) => {
		if (deviceId in this.unsubscribers) this.unsubscribers[deviceId]?.()
		if (deviceId in this.runs) this.runs[deviceId] = this.STATE_DEFAULT
	}

	/**
	 * Close the calibration overlay for the spcified device id
	 * @param deviceId
	 * @returns
	 */
	close = (deviceId: Device["id"]) => {
		if (!(deviceId in this.overlayIds)) return
		closeOverlay(this.overlayIds[deviceId])
	}

	/**
	 * Cancel a running calibration for the specified device
	 * @param device
	 */
	cancel = (device: Device) => {
		this.reset(device.id)
		this.close(device.id)

		DeviceCalibrationManager.cancelCalibration(device)
	}

	/**
	 * Begin calibration for the specified device
	 * @param device
	 */
	calibrate(device: Device) {
		const store = DeviceCalibrationManager.startCalibration(device, this.onSuccess, this.onFailure)
		this.unsubscribers[device.id] = store.subscribe((progress) => {
			this.runs[device.id].progress = { ...progress }
			this.runs[device.id].step = CalibrationStep.Loading
		})
	}

	/**
	 * Checks for any active calibration, then subscribes to it
	 * @param device
	 */
	async checkForRunningCalibration(device: Device) {
		const store = await DeviceCalibrationManager.resumeCalibrationIfRunning(
			device,
			this.onSuccess,
			this.onFailure
		)
		if (store) {
			if (!(device.id in this.runs)) this.runs[device.id] = this.STATE_DEFAULT
			this.unsubscribers[device.id] = store.subscribe((progress) => {
				this.runs[device.id].progress = { ...progress }
				this.runs[device.id].step = CalibrationStep.Loading
			})
			openMinimizedCalibration(device)
		}
	}

	/**
	 * Rerun calibration processing for the specified device & calibration id
	 * @param device
	 * @param calId
	 */
	rerun = (device: Device, calId: number) => {
		this.reset(device.id)
		const store = DeviceCalibrationManager.rerunCalibrationProcessing(
			device,
			calId,
			this.onSuccess,
			this.onFailure
		)
		this.unsubscribers[device.id] = store.subscribe((progress) => {
			this.runs[device.id].progress = { ...progress }
		})
	}

	/**
	 * Gets the calibration images for the specified device and goes to the troubleshooter
	 * @param device
	 * @returns
	 */
	userUnsatisfied = async (device: Device) => {
		console.log("user unsatisfied!!")

		if (!(device.id in this.runs)) return

		const images = await DeviceCalibrationManager.getCalibrationImages(device)

		this.runs[device.id].step = -2
		this.runs[device.id].failImages = images
	}

	/**
	 * Triggered on a successful calibration
	 */
	private onSuccess = (snapshot: Snapshot, deviceId: number) => {
		DataHandlerCalibration.pull()
		DataHandlerSnapshot.pull()

		if (!(deviceId in this.runs)) return

		this.runs[deviceId].step = CalibrationStep.Finished
		this.runs[deviceId].snapshot = snapshot
	}

	/**
	 * Triggered on a failed calibration
	 * @param images an array of calibration images
	 * @param deviceId the device id for the failed calibration
	 * @returns
	 */
	private onFailure = (images: Array<string> | undefined, deviceId: number) => {
		if (!(deviceId in this.runs)) return

		this.runs[deviceId].step = CalibrationStep.Failed
		this.runs[deviceId].failImages = images
	}
}

export const CalibrationController = new CalController()
